import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { useSupabaseQuery } from '../hooks/useSupabase';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAppStore } from '../stores/appStore';
import type { Contact } from '../types';

const MOCK_CONTACTS: Contact[] = [
  { id: '1', org_id: 'demo', phone: '994 50 123-45-67', whatsapp_id: '994501234567@c.us', first_name: 'Ali', last_name: 'Aliyev', company: 'Digitix', title: 'CEO', email: null, tags: ['VIP', 'Cold'], qualification: 'new', lead_score: 0, custom_fields: {}, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: '2', org_id: 'demo', phone: '994 55 987-65-43', whatsapp_id: '994559876543@c.us', first_name: 'Sara', last_name: 'Hasanova', company: 'SoftTech', title: 'HR', email: null, tags: ['Warm'], qualification: 'new', lead_score: 0, custom_fields: {}, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
];

function formatAzeriPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 12) return phone;
  const country = digits.slice(0, 3);
  const prefix = digits.slice(3, 5);
  const part1 = digits.slice(5, 8);
  const part2 = digits.slice(8, 10);
  const part3 = digits.slice(10, 12);
  return `${country} ${prefix} ${part1}-${part2}-${part3}`;
}

function cleanPhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

const EMPTY_FORM = { id: '', first_name: '', last_name: '', phone: '', tags: [] as string[] };

export default function Contacts(): React.JSX.Element {
  const orgId = useAppStore(s => s.orgId);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const csvRef = useRef<HTMLInputElement>(null);

  const { data, loading, error, refetch } = useSupabaseQuery<Contact[]>(
    async () => {
      if (!isSupabaseConfigured) return { data: MOCK_CONTACTS, error: null };
      const { data: rows, error: err } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);
      return { data: rows as Contact[] | null, error: err };
    },
    []
  );

  const contacts = data ?? (isSupabaseConfigured ? [] : MOCK_CONTACTS);
  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
    return name.includes(q) || c.phone.includes(q) || c.tags.some(t => t.toLowerCase().includes(q));
  });

  const handleSaveContact = async () => {
    const cleaned = cleanPhone(form.phone);
    if (cleaned.length < 10) { toast.error('Valid phone number is required'); return; }
    if (!isSupabaseConfigured) { toast.info('Connect Supabase to save contacts'); return; }

    setSaving(true);
    try {
      const contactData = {
        org_id: orgId!,
        phone: formatAzeriPhone(cleaned),
        whatsapp_id: `${cleaned}@c.us`,
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        tags: form.tags,
      };

      if (form.id) {
        const { error: err } = await supabase.from('contacts').update(contactData).eq('id', form.id);
        if (err) throw err;
        toast.success('Contact updated');
      } else {
        const { error: err } = await supabase.from('contacts').insert(contactData);
        if (err) throw err;
        toast.success('Contact added');
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    if (!isSupabaseConfigured) { toast.info('Connect Supabase to delete contacts'); return; }
    const { error: err } = await supabase.from('contacts').delete().eq('id', id);
    if (err) toast.error(err.message);
    else { toast.success('Contact deleted'); refetch(); }
  };

  const handleAddTag = (e?: React.KeyboardEvent) => {
    if (e && e.key !== 'Enter') return;
    if (e) e.preventDefault();
    const tag = tagInput.trim();
    if (!tag || form.tags.includes(tag)) return;
    setForm(f => ({ ...f, tags: [...f.tags, tag] }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }));
  };

  const handleCSVImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isSupabaseConfigured) { toast.info('Connect Supabase to import contacts'); return; }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target?.result as string;
        const lines = text.trim().split('\n').filter(l => l.trim());
        if (lines.length < 2) { toast.error('CSV must have header and data'); return; }

        // Expected: Name, Surname, Phone, Tags
        const rows = lines.slice(1).map(line => {
          const parts = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const phoneRaw = parts[2] || '';
          const cleaned = cleanPhone(phoneRaw);
          if (!cleaned) return null;

          return {
            org_id: orgId!,
            first_name: parts[0] || null,
            last_name: parts[1] || null,
            phone: formatAzeriPhone(cleaned),
            whatsapp_id: `${cleaned}@c.us`,
            tags: parts[3] ? parts[3].split('|').map(t => t.trim()) : [], // using | for multi-tag in CSV cell or just split by comma if line uses semicolon
          };
        }).filter(Boolean);

        if (!rows.length) { toast.error('No valid rows found'); return; }
        const { error: err } = await supabase.from('contacts').insert(rows as any);
        if (err) throw err;
        toast.success(`Imported ${rows.length} contacts`);
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Import failed');
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-dx-text m-0">Contacts</h2>
          <p className="text-[13px] text-dx-muted mt-1 mb-0">{filtered.length} total contacts</p>
        </div>
        <div className="flex gap-2.5">
          <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleCSVImport} />
          <button onClick={() => csvRef.current?.click()} className="bg-dx-surface border border-dx-border rounded-xl px-4 py-2 text-[13px] text-dx-text hover:border-dx-accent transition-colors">Import CSV</button>
          <button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="px-5 py-2.5 rounded-xl border-none text-[13px] font-semibold text-black bg-[linear-gradient(135deg,#25D366,#1BA350)]">+ Add Contact</button>
        </div>
      </div>

      <div className="flex gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, phone or tag..."
          className="flex-1 bg-dx-surface border border-dx-border rounded-xl px-4 py-2.5 text-[13px] text-dx-text outline-none focus:border-dx-accent transition-colors"
        />
      </div>

      <div className="bg-dx-surface border border-dx-border rounded-2xl overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-dx-border bg-dx-bg/50">
              {["Name", "Surname", "Phone", "Tags", ""].map((h, i) => (
                <th key={i} className="text-left px-5 py-3.5 text-xs font-semibold text-dx-muted uppercase tracking-[0.5px]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-20 text-center"><div className="w-6 h-6 border-2 border-dx-accent border-t-transparent rounded-full animate-spin inline-block" /></td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className="border-b border-dx-border/30 hover:bg-[#1A1A25] transition-colors group">
                <td className="px-5 py-4 text-[13px] font-medium text-dx-text">{c.first_name}</td>
                <td className="px-5 py-4 text-[13px] text-dx-text">{c.last_name}</td>
                <td className="px-5 py-4 text-[13px] font-mono text-dx-dim">{c.phone}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1.5">
                    {c.tags.map((t, idx) => (
                      <span key={idx} className="px-2 py-0.5 rounded-md bg-dx-accent/10 border border-dx-accent/20 text-[10px] text-dx-accent font-medium">{t}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setForm({ id: c.id, first_name: c.first_name || '', last_name: c.last_name || '', phone: c.phone, tags: c.tags }); setShowModal(true); }} className="p-1 px-2 text-xs text-dx-accent hover:bg-dx-accent/10 rounded">Edit</button>
                    <button onClick={() => handleDelete(c.id)} className="p-1 px-2 text-xs text-dx-danger hover:bg-dx-danger/10 rounded">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-dx-surface border border-dx-border rounded-2xl w-full max-w-md shadow-2xl p-6 overflow-hidden">
            <h3 className="text-lg font-bold text-dx-text mb-6">{form.id ? 'Edit Contact' : 'New Contact'}</h3>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-dx-muted mb-1.5 block">Name</label>
                  <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-3 py-2 text-[13px] text-dx-text outline-none focus:border-dx-accent" placeholder="Ali" />
                </div>
                <div>
                  <label className="text-xs text-dx-muted mb-1.5 block">Surname</label>
                  <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-3 py-2 text-[13px] text-dx-text outline-none focus:border-dx-accent" placeholder="Aliyev" />
                </div>
              </div>
              <div>
                <label className="text-xs text-dx-muted mb-1.5 block">Phone (994 XX XXX-XX-XX)</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-3 py-2 text-[13px] text-dx-text outline-none focus:border-dx-accent" placeholder="994501234567" />
              </div>

              {/* Tag Editor */}
              <div>
                <label className="text-xs text-dx-muted mb-1.5 block">Tags</label>
                <div className="flex flex-wrap gap-2 p-2 min-h-[42px] bg-dx-bg border border-dx-border rounded-xl">
                  {form.tags.map((t, i) => (
                    <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded bg-dx-accent/20 text-[11px] text-dx-accent">
                      {t} <button onClick={() => removeTag(t)} className="hover:text-white">✕</button>
                    </span>
                  ))}
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Add tag..."
                    className="flex-1 min-w-[60px] bg-transparent border-none outline-none text-[11px] text-dx-text"
                  />
                </div>
                <p className="text-[10px] text-dx-dim mt-1.5">Press Enter to add tag</p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button disabled={saving} onClick={handleSaveContact} className="flex-1 py-2.5 rounded-xl font-semibold text-black bg-[linear-gradient(135deg,#25D366,#1BA350)] disabled:opacity-50">{saving ? 'Saving...' : 'Save Contact'}</button>
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl border border-dx-border text-[13px] text-dx-text">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
